import { toDayKey } from './day-key.js';
import { resolveCoordinates, resolveCoordinatesFast } from './map-coords.js';
import { searchPlaces } from './place-search.js';
import { formatDateIT } from './format.js';
import { resolveStopDescription } from './stop-descriptions.js';

const geocodeCache = new Map();

const STOP_WORDS = new Set([
  'il', 'lo', 'la', 'le', 'gli', 'un', 'una', 'di', 'da', 'del', 'della', 'dei', 'delle',
  'a', 'al', 'alla', 'allo', 'in', 'nel', 'nella', 'per', 'con', 'su', 'che', 'non', 'sara',
  'sarai', 'siamo', 'siete', 'andremo', 'andate', 'visiteremo', 'visitiamo', 'passeremo',
  'quindi', 'questo', 'questa', 'posto', 'luogo', 'nel', 'nostro', 'itinerario', 'programma',
  'previsto', 'giorno', 'dom', 'planinski',
]);

function normalizeText(text) {
  return String(text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(text) {
  return normalizeText(text)
    .split(' ')
    .filter((token) => token.length > 2 && !STOP_WORDS.has(token));
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function buildItineraryIndex(ctx) {
  const entries = [];
  for (const [index, day] of (ctx.days ?? []).entries()) {
    const dayNumber = index + 1;

    for (const stop of day.stops ?? []) {
      const fast = resolveCoordinatesFast(stop);
      const description = stop.description || resolveStopDescription(stop) || '';
      entries.push({
        dayNumber,
        dayKey: toDayKey(day.date),
        dateLabel: formatDateIT(day.date),
        dayTitle: day.title,
        stopName: stop.name,
        stop: { ...stop, description },
        searchBlob: normalizeText(
          [day.title, day.ritmo, stop.name, description].filter(Boolean).join(' '),
        ),
        stopBlob: normalizeText([stop.name, description].filter(Boolean).join(' ')),
        lat: fast?.lat ?? null,
        lng: fast?.lng ?? null,
      });
    }
  }
  return entries;
}

function scoreTextMatch(queryTokens, searchBlob) {
  if (!queryTokens.length || !searchBlob) return 0;
  let score = 0;
  for (const token of queryTokens) {
    if (!searchBlob.includes(token)) continue;
    score += token.length >= 5 ? 4 : token.length >= 4 ? 3 : 1;
  }
  return score;
}

function extractPlaceQuery(userMessage) {
  const text = String(userMessage ?? '').trim();
  const patterns = [
    /(?:andremo|andate|visiteremo|visitiamo|passeremo|passate)\s+(?:a|al|alla|allo)\s+(.+?)\??$/i,
    /(?:la|il|lo)\s+(.+?)\s+(?:la|lo|li|le)\s+(?:visiteremo|visitiamo|vedremo|vediamo)/i,
    /(?:posto|luogo)\s+(.+?)\??$/i,
    /^(.+?)\s+(?:è|e)\s+(?:nel|nello|nella)\s+(?:nostro|mio)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1].replace(/\?+$/, '').trim();
  }

  return text.replace(/\?+$/, '').trim();
}

function stopNameMatchesQuery(placeQuery, stopName) {
  const query = normalizeText(placeQuery);
  const name = normalizeText(stopName);
  if (!query || !name) return false;
  return name.includes(query) || query.includes(name);
}

function matchedDistinctiveTokens(queryTokens, blob) {
  return queryTokens.filter((token) => token.length >= 4 && blob.includes(token));
}

function isStrongTextMatch(queryTokens, entry, score) {
  const matched = matchedDistinctiveTokens(queryTokens, entry.stopBlob);
  if (matched.length >= 2) return true;
  if (matched.length === 1 && matched[0].length >= 6) return true;
  if (score >= 4 && matched.length > 0) return true;
  return false;
}

function formatKm(km) {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${Math.round(km)} km`;
}

async function ensureEntryCoords(entry, country) {
  if (Number.isFinite(entry.lat) && Number.isFinite(entry.lng)) return entry;

  const cacheKey = `${country}::${entry.stopName}`;
  if (geocodeCache.has(cacheKey)) {
    const cached = geocodeCache.get(cacheKey);
    if (cached) {
      entry.lat = cached.lat;
      entry.lng = cached.lng;
    }
    return entry;
  }

  const coords = await resolveCoordinates(entry.stop, { country });
  geocodeCache.set(cacheKey, coords);
  if (coords) {
    entry.lat = coords.lat;
    entry.lng = coords.lng;
  }
  return entry;
}

async function hydrateItineraryCoords(index, country) {
  for (const entry of index) {
    await ensureEntryCoords(entry, country);
  }
}

function minDistanceToItinerary(place, index) {
  let nearest = null;
  for (const entry of index) {
    if (!Number.isFinite(entry.lat) || !Number.isFinite(entry.lng)) continue;
    const km = haversineKm(place.lat, place.lng, entry.lat, entry.lng);
    if (!nearest || km < nearest.km) nearest = { entry, km };
  }
  return nearest;
}

async function resolveQueriedPlace(placeQuery, country, index) {
  const queries = [
    placeQuery,
    country ? `${placeQuery}, ${country}` : null,
    country ? `${placeQuery}, Julian Alps, ${country}` : null,
  ].filter(Boolean);

  const seen = new Set();
  const candidates = [];

  for (const query of queries) {
    const hits = await searchPlaces(query, { country, limit: 5 });
    for (const hit of hits) {
      const key = `${hit.lat},${hit.lng}`;
      if (seen.has(key)) continue;
      seen.add(key);
      candidates.push(hit);
    }
    if (candidates.length >= 3) break;
  }

  if (!candidates.length) return null;
  if (candidates.length === 1) return candidates[0];

  await hydrateItineraryCoords(index, country);

  let bestPlace = candidates[0];
  let bestNearest = minDistanceToItinerary(bestPlace, index);

  for (const candidate of candidates.slice(1)) {
    const nearest = minDistanceToItinerary(candidate, index);
    const bestKm = bestNearest?.km ?? Infinity;
    const candidateKm = nearest?.km ?? Infinity;
    if (candidateKm < bestKm) {
      bestPlace = candidate;
      bestNearest = nearest;
    }
  }

  return bestPlace;
}

function buildProximityVerdict(placeQuery, queriedPlace, nearest) {
  const { entry, km } = nearest;

  if (km <= 3) {
    return {
      placeQuery,
      verdict: 'same_area',
      geocodedName: queriedPlace.name,
      dayNumber: entry.dayNumber,
      dateLabel: entry.dateLabel,
      dayTitle: entry.dayTitle,
      nearestStop: entry.stopName,
      distanceKm: Math.round(km * 10) / 10,
      note: `«${queriedPlace.name}» non è una tappa con quel nome esatto, ma è a ~${formatKm(km)} da «${entry.stopName}» (giorno ${entry.dayNumber}, ${entry.dateLabel}) — stessa area escursionistica. Giorno/tappa più vicini su tutto l'itinerario.`,
    };
  }

  if (km <= 25) {
    return {
      placeQuery,
      verdict: 'nearby',
      geocodedName: queriedPlace.name,
      dayNumber: entry.dayNumber,
      dateLabel: entry.dateLabel,
      dayTitle: entry.dayTitle,
      nearestStop: entry.stopName,
      distanceKm: Math.round(km * 10) / 10,
      note: `«${queriedPlace.name}» non è tappa dedicata, ma il giorno ${entry.dayNumber} (${entry.dateLabel}) sarete a ~${formatKm(km)} (tappa «${entry.stopName}»). Giorno/tappa più vicini su tutto l'itinerario.`,
    };
  }

  if (km <= 80) {
    return {
      placeQuery,
      verdict: 'far',
      geocodedName: queriedPlace.name,
      dayNumber: entry.dayNumber,
      dateLabel: entry.dateLabel,
      nearestStop: entry.stopName,
      distanceKm: Math.round(km * 10) / 10,
      note: `«${queriedPlace.name}» non è in programma; il giorno più vicino è il ${entry.dayNumber} ma a ~${formatKm(km)}.`,
    };
  }

  return {
    placeQuery,
    verdict: 'outside',
    geocodedName: queriedPlace.name,
    nearestStop: entry.stopName,
    dayNumber: entry.dayNumber,
    distanceKm: Math.round(km * 10) / 10,
    note: `«${queriedPlace.name}» risulta lontano dall'itinerario (tappa più vicina «${entry.stopName}» a ~${formatKm(km)}).`,
  };
}

/**
 * Deterministic place analysis to guide the LLM (synonyms, proximity).
 * @param {string} userMessage
 * @param {object} ctx
 */
export async function analyzePlaceQuestion(userMessage, ctx) {
  const placeQuery = extractPlaceQuery(userMessage);
  const queryTokens = tokenize(placeQuery);
  const index = buildItineraryIndex(ctx);
  const country = ctx?.trip?.country ?? '';

  if (!placeQuery || !queryTokens.length) {
    return { placeQuery, verdict: 'general', note: 'Domanda generica senza luogo specifico' };
  }

  const scored = index
    .map((entry) => ({
      entry,
      score: Math.max(scoreTextMatch(queryTokens, entry.stopBlob), scoreTextMatch(queryTokens, entry.searchBlob)),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  const best = scored[0];

  if (best && stopNameMatchesQuery(placeQuery, best.entry.stopName)) {
    return {
      placeQuery,
      verdict: 'in_itinerary',
      dayNumber: best.entry.dayNumber,
      dateLabel: best.entry.dateLabel,
      dayTitle: best.entry.dayTitle,
      stopName: best.entry.stopName,
      note: `Il luogo corrisponde alla tappa «${best.entry.stopName}» (giorno ${best.entry.dayNumber}, ${best.entry.dateLabel}).`,
    };
  }

  if (best && isStrongTextMatch(queryTokens, best.entry, best.score)) {
    return {
      placeQuery,
      verdict: 'on_route',
      dayNumber: best.entry.dayNumber,
      dateLabel: best.entry.dateLabel,
      dayTitle: best.entry.dayTitle,
      stopName: best.entry.stopName,
      matchedTokens: matchedDistinctiveTokens(queryTokens, best.entry.stopBlob),
      note: `«${placeQuery}» è citato nel percorso/descrizione della tappa «${best.entry.stopName}» (giorno ${best.entry.dayNumber}, ${best.entry.dateLabel}). Non dire che è fuori rotta.`,
    };
  }

  const queriedPlace = await resolveQueriedPlace(placeQuery, country, index);
  if (!queriedPlace) {
    if (best) {
      return {
        placeQuery,
        verdict: 'possible_match',
        dayNumber: best.entry.dayNumber,
        dateLabel: best.entry.dateLabel,
        dayTitle: best.entry.dayTitle,
        stopName: best.entry.stopName,
        note: `Possibile collegamento con «${best.entry.stopName}» (giorno ${best.entry.dayNumber}). Non affermare che sia fuori rotta senza chiarire il dubbio.`,
      };
    }
    return {
      placeQuery,
      verdict: 'unknown',
      note: 'Luogo non geocodificabile e senza corrispondenza testuale nell\'itinerario.',
    };
  }

  await hydrateItineraryCoords(index, country);
  const nearest = minDistanceToItinerary(queriedPlace, index);

  if (!nearest) {
    return {
      placeQuery,
      verdict: 'unknown',
      geocodedName: queriedPlace.name,
      note: 'Luogo geocodificato ma impossibile stimare distanze dalle tappe.',
    };
  }

  return buildProximityVerdict(placeQuery, queriedPlace, nearest);
}
